import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, type ApiError, useApiMutation } from "@/shared/api";
import type {
  IMembershipLevel,
  MembershipBillingType,
} from "../interfaces/membership.interface";
import { normalizeMembershipLevelsResponse } from "./normalize-membership-levels";
import { buildPaymentSuccessCallbackUrl } from "../lib/payment-callback";

type ApiQueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, readonly unknown[]>,
  "queryKey" | "queryFn"
>;
export type MembershipLevelsResponse = IMembershipLevel[];

const membershipLevelsRequest = {
  url: "/membership/levels",
  method: "get" as const,
};

export const getMembershipLevels =
  async (): Promise<MembershipLevelsResponse> =>
    normalizeMembershipLevelsResponse(
      await apiRequest<unknown>(membershipLevelsRequest)
    );

export const useMembershipLevels = (
  options?: ApiQueryOptions<MembershipLevelsResponse>
) =>
  useQuery<MembershipLevelsResponse, ApiError>({
    queryKey: ["membership-levels"],
    queryFn: getMembershipLevels,
    ...options,
  });

export type SetupMembershipPayload = {
  membershipPlanId: string;
  billingType: MembershipBillingType;
  callbackUrl?: string;
};

export type SetupMembershipResponse = {
  status?: "ACTIVE" | "PENDING" | "FAILED" | string;
  kycUrl?: string | null;
  eduKycUrl?: string | null;
  membership?: {
    status?: "ACTIVE" | "PENDING" | "FAILED" | string;
    planName?: string | null;
    billingType?: MembershipBillingType | string | null;
    expiredAt?: string | null;
  } | null;
  plan?: {
    name: string;
    type: "FREE" | "PAID" | string;
    billingType: MembershipBillingType | string;
    amount: number;
    currency: string;
  };
  paymentUrl?: string | null;
};

const setupMembershipRequest = (payload: SetupMembershipPayload) => ({
  url: "/membership/setup",
  method: "post" as const,
  data: {
    ...payload,
    callbackUrl:
      payload.callbackUrl ??
      (typeof window !== "undefined"
        ? buildPaymentSuccessCallbackUrl(window.location.href)
        : undefined),
  },
});

export const setupMembership = (payload: SetupMembershipPayload) =>
  apiRequest<SetupMembershipResponse>(setupMembershipRequest(payload));

export const useSetupMembership = (
  options?: UseMutationOptions<
    SetupMembershipResponse,
    ApiError,
    SetupMembershipPayload
  >
) => {
  const queryClient = useQueryClient();

  return useApiMutation<SetupMembershipResponse, SetupMembershipPayload>(
    setupMembershipRequest,
    {
      ...options,
      onSuccess: (data, variables, context, mutation) => {
        queryClient.invalidateQueries({ queryKey: ["membership-status"] });
        options?.onSuccess?.(data, variables, context, mutation);
      },
    }
  );
};

export type MembershipStatusResponse = SetupMembershipResponse;

const membershipStatusRequest = {
  url: "/profile/membership",
  method: "get" as const,
};

export const getMembershipStatus = () =>
  apiRequest<MembershipStatusResponse>(membershipStatusRequest);

export const useMembershipStatus = (
  options?: ApiQueryOptions<MembershipStatusResponse>
) =>
  useQuery<MembershipStatusResponse, ApiError>({
    queryKey: ["membership-status"],
    queryFn: getMembershipStatus,
    ...options,
  });

export type StripeWebhookPayload = {
  id: string;
  object: string;
  type: string;
  data: Record<string, unknown>;
};

export type StripeWebhookOptions = {
  signature?: string;
};

const stripeWebhookRequest = (
  payload: StripeWebhookPayload,
  options?: StripeWebhookOptions
) => ({
  url: "/payments/webhook/stripe",
  method: "post" as const,
  data: payload,
  headers: options?.signature
    ? {
        "stripe-signature": options.signature,
      }
    : undefined,
});

export const sendStripeWebhookEvent = (
  payload: StripeWebhookPayload,
  options?: StripeWebhookOptions
) => apiRequest(stripeWebhookRequest(payload, options));
